/**
 * Note Events Logger
 *
 * Handles logging of note-related events to PostHog Analytics.
 * @module features/analytics/utils/noteEventLogger
 */
import { ANALYTICS_EVENTS } from "../constants";
import type {
  TNoteCreatedParams,
  TNoteDeletedParams,
  INoteAnalyticsService,
} from "../types";
import { sendAnalyticsEvent } from "./posthogSender";

/**
 * Note events analytics logger.
 *
 * Provides methods for logging note creation and deletion events.
 * @example
 * ```typescript
 * import { noteEventLogger } from '@/features/analytics';
 *
 * noteEventLogger.logNoteCreated({
 *   note_id: 'note123',
 *   user_id: 'user456',
 *   // ... other params
 * });
 * ```
 */
export const noteEventLogger: INoteAnalyticsService = {
  /**
   * Logs a note creation event.
   * @param params - Note created parameters
   */
  logNoteCreated: async (params: TNoteCreatedParams): Promise<void> => {
    await sendAnalyticsEvent(ANALYTICS_EVENTS.NOTE.CREATED, params);
  },

  /**
   * Logs a note deletion event.
   * @param params - Note deleted parameters
   */
  logNoteDeleted: async (params: TNoteDeletedParams): Promise<void> => {
    await sendAnalyticsEvent(ANALYTICS_EVENTS.NOTE.DELETED, params);
  },
};
